import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  LinearProgress,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Collapse,
  Alert,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  Grid,
  useTheme,
  alpha,
  styled
} from '@mui/material';
import {
  ExpandMore,
  ExpandLess,
  Refresh,
  Error as ErrorIcon,
  CheckCircle,
  CloudUpload,
  AutorenewRounded as Processing,
  Warning,
  Timeline,
  Download,
  MoreVert
} from '@mui/icons-material';
import auditService, {
  AuditEventType,
  AuditSeverity,
  ProcessingStage,
  FileOperationStatus,
  AuditEvent
} from '../services/audit/auditService';

// Styled components
const StyledCard = styled(Card)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  transition: theme.transitions.create(['box-shadow', 'transform'], {
    duration: theme.transitions.duration.short,
  }),
  '&:hover': {
    boxShadow: theme.shadows[4],
    transform: 'translateY(-2px)',
  }
}));

const ProgressContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(2),
  marginTop: theme.spacing(1),
  marginBottom: theme.spacing(1)
}));

const StageChip = styled(Chip)<{ stage: ProcessingStage }>(({ theme, stage }) => {
  const getStageColor = (stage: ProcessingStage) => {
    switch (stage) {
      case ProcessingStage.QUEUED:
        return theme.palette.grey;
      case ProcessingStage.UPLOADING:
        return theme.palette.info;
      case ProcessingStage.UPLOADED:
        return theme.palette.success;
      case ProcessingStage.VALIDATING:
      case ProcessingStage.PARSING:
        return theme.palette.warning;
      case ProcessingStage.PROCESSING:
      case ProcessingStage.ANALYZING:
        return theme.palette.primary;
      case ProcessingStage.COMPLETED:
        return theme.palette.success;
      case ProcessingStage.FAILED:
        return theme.palette.error;
      case ProcessingStage.RETRY:
        return theme.palette.warning;
      default:
        return theme.palette.grey;
    }
  };

  const color = getStageColor(stage);
  const colorMain = 'main' in color ? color.main : color[500] || '#666';
  
  return {
    backgroundColor: alpha(colorMain, 0.1),
    color: colorMain,
    fontWeight: 600,
    '& .MuiChip-icon': {
      color: colorMain,
    }
  };
});

// Component props
interface FileProgressTrackerProps {
  clientId?: string;
  workflowId?: string;
  showCompleted?: boolean;
  compact?: boolean;
  maxItems?: number;
  onOperationClick?: (operation: FileOperationStatus) => void;
}

// Individual file operation component
interface FileOperationItemProps {
  operation: FileOperationStatus;
  showDetails?: boolean;
  onToggleDetails?: () => void;
  onRetry?: (fileId: string) => void;
  compact?: boolean;
}

const FileOperationItem: React.FC<FileOperationItemProps> = ({
  operation,
  showDetails = false,
  onToggleDetails,
  onRetry,
  compact = false
}) => {
  const theme = useTheme();
  const [recentEvents, setRecentEvents] = useState<AuditEvent[]>([]);

  useEffect(() => {
    // Get recent events for this file
    const events = auditService.getRecentEvents(50)
      .filter(event => event.fileId === operation.fileId)
      .slice(0, 10);
    setRecentEvents(events);
  }, [operation.fileId]);

  const getStageIcon = (stage: ProcessingStage) => {
    switch (stage) {
      case ProcessingStage.UPLOADING:
        return <CloudUpload />;
      case ProcessingStage.PROCESSING:
      case ProcessingStage.ANALYZING:
        return <Processing />;
      case ProcessingStage.COMPLETED:
        return <CheckCircle />;
      case ProcessingStage.FAILED:
        return <ErrorIcon />;
      case ProcessingStage.RETRY:
        return <Refresh />;
      default:
        return <Timeline />;
    }
  };

  const getProgressColor = (stage: ProcessingStage, progress: number) => {
    if (stage === ProcessingStage.FAILED) return 'error';
    if (stage === ProcessingStage.COMPLETED) return 'success';
    if (progress > 75) return 'primary';
    if (progress > 25) return 'info';
    return 'warning';
  };

  const formatDuration = (startTime: string, endTime?: string) => {
    const start = new Date(startTime).getTime();
    const end = endTime ? new Date(endTime).getTime() : Date.now();
    const duration = end - start;
    
    if (duration < 60000) {
      return `${Math.round(duration / 1000)}s`;
    } else if (duration < 3600000) {
      return `${Math.round(duration / 60000)}m`;
    } else {
      return `${Math.round(duration / 3600000)}h`;
    }
  };

  const formatBytes = (bytes: number) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${(bytes / Math.pow(k, i)).toFixed(1)} ${sizes[i]}`;
  };

  if (compact) {
    return (
      <Box sx={{ p: 1, borderBottom: `1px solid ${theme.palette.divider}` }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box sx={{ minWidth: 20 }}>{getStageIcon(operation.stage)}</Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="body2" noWrap>{operation.fileName}</Typography>
            <Typography variant="caption" color="text.secondary">
              {operation.stage} • {operation.progress}%
            </Typography>
          </Box>
          <StageChip 
            stage={operation.stage}
            label={operation.stage}
            size="small"
            icon={getStageIcon(operation.stage)}
          />
        </Box>
        {operation.progress < 100 && operation.stage !== ProcessingStage.FAILED && (
          <LinearProgress
            variant="determinate"
            value={operation.progress}
            color={getProgressColor(operation.stage, operation.progress)}
            sx={{ mt: 0.5, height: 4, borderRadius: 2 }}
          />
        )}
      </Box>
    );
  }

  return (
    <StyledCard elevation={1}>
      <CardContent sx={{ pb: 1 }}>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
          <Box sx={{ minWidth: 24 }}>{getStageIcon(operation.stage)}</Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="subtitle2" noWrap>
              {operation.fileName}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {operation.dataSource} • Started {formatDuration(operation.startTime)} ago
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <StageChip 
              stage={operation.stage}
              label={operation.stage}
              size="small"
              icon={getStageIcon(operation.stage)}
            />
            {operation.errorCount > 0 && (
              <Chip
                icon={<Warning />}
                label={`${operation.errorCount} errors`}
                size="small"
                color="error"
                variant="outlined"
              />
            )}
            {onToggleDetails && (
              <IconButton size="small" onClick={onToggleDetails}>
                {showDetails ? <ExpandLess /> : <ExpandMore />}
              </IconButton>
            )}
          </Box>
        </Box>

        {/* Progress */}
        {operation.progress < 100 && operation.stage !== ProcessingStage.FAILED && (
          <ProgressContainer>
            <LinearProgress
              variant="determinate"
              value={operation.progress}
              color={getProgressColor(operation.stage, operation.progress)}
              sx={{ flex: 1, height: 8, borderRadius: 4 }}
            />
            <Typography variant="body2" sx={{ minWidth: 40 }}>
              {operation.progress}%
            </Typography>
          </ProgressContainer>
        )}

        {/* Current Operation */}
        {operation.currentOperation && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {operation.currentOperation}
          </Typography>
        )}

        {/* File Size Info */}
        {operation.totalBytes && (
          <Typography variant="caption" color="text.secondary">
            {operation.processedBytes 
              ? `${formatBytes(operation.processedBytes)} / ${formatBytes(operation.totalBytes)}`
              : formatBytes(operation.totalBytes)
            }
          </Typography>
        )}

        {/* Estimated Completion */}
        {operation.estimatedCompletion && operation.stage !== ProcessingStage.COMPLETED && (
          <Typography variant="caption" color="text.secondary" display="block">
            Estimated completion: {new Date(operation.estimatedCompletion).toLocaleTimeString()}
          </Typography>
        )}

        {/* Error State */}
        {operation.stage === ProcessingStage.FAILED && (
          <Box sx={{ mt: 1 }}>
            <Alert 
              severity="error" 
              action={
                onRetry && (
                  <Button 
                    size="small" 
                    onClick={() => onRetry(operation.fileId)}
                    startIcon={<Refresh />}
                  >
                    Retry
                  </Button>
                )
              }
            >
              Operation failed after {operation.retryCount} retries
            </Alert>
          </Box>
        )}

        {/* Details */}
        <Collapse in={showDetails}>
          <Box sx={{ mt: 2, pt: 2, borderTop: `1px solid ${theme.palette.divider}` }}>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
              Recent Activity
            </Typography>
            <List dense>
              {recentEvents.map((event, index) => (
                <ListItem key={event.id} sx={{ py: 0.5 }}>
                  <ListItemText
                    primary={
                      <Typography variant="body2">
                        {event.message}
                      </Typography>
                    }
                    secondary={
                      <Typography variant="caption" color="text.secondary">
                        {new Date(event.timestamp).toLocaleTimeString()} • {event.severity}
                      </Typography>
                    }
                  />
                  <ListItemSecondaryAction>
                    <Chip 
                      label={event.eventType.replace(/_/g, ' ')}
                      size="small"
                      variant="outlined"
                      sx={{ fontSize: '0.7rem' }}
                    />
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
            </List>
          </Box>
        </Collapse>
      </CardContent>
    </StyledCard>
  );
};

// Main component
export const FileProgressTracker: React.FC<FileProgressTrackerProps> = ({
  clientId,
  workflowId,
  showCompleted = true,
  compact = false,
  maxItems = 20,
  onOperationClick
}) => {
  const [operations, setOperations] = useState<FileOperationStatus[]>([]);
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set());
  const [refreshing, setRefreshing] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(true);

  // Load operations
  const loadOperations = useCallback(() => {
    let allOperations = auditService.getActiveOperations();
    
    // Filter by client/workflow if specified
    if (clientId) {
      allOperations = allOperations.filter(op => op.clientId === clientId);
    }
    if (workflowId) {
      allOperations = allOperations.filter(op => op.workflowId === workflowId);
    }

    // Include completed operations if requested
    if (!showCompleted) {
      allOperations = allOperations.filter(op => 
        op.stage !== ProcessingStage.COMPLETED && op.stage !== ProcessingStage.FAILED
      );
    }

    // Sort by last update (most recent first)
    allOperations.sort((a, b) => 
      new Date(b.lastUpdate).getTime() - new Date(a.lastUpdate).getTime()
    );

    // Limit items
    if (maxItems) {
      allOperations = allOperations.slice(0, maxItems);
    }

    setOperations(allOperations);
  }, [clientId, workflowId, showCompleted, maxItems]);

  // Setup event listeners
  useEffect(() => {
    const handleAuditEvent = (event: AuditEvent) => {
      // Refresh operations when file events occur
      if (event.eventType.startsWith('file_') || event.eventType.includes('processing')) {
        loadOperations();
      }
    };

    // Listen to relevant events
    const eventTypes = [
      AuditEventType.FILE_UPLOAD_START,
      AuditEventType.FILE_UPLOAD_PROGRESS,
      AuditEventType.FILE_UPLOAD_COMPLETE,
      AuditEventType.FILE_UPLOAD_ERROR,
      AuditEventType.FILE_PROCESSING_START,
      AuditEventType.FILE_PROCESSING_PROGRESS,
      AuditEventType.FILE_PROCESSING_COMPLETE,
      AuditEventType.FILE_PROCESSING_ERROR
    ];

    eventTypes.forEach(eventType => {
      auditService.addEventListener(eventType, handleAuditEvent);
    });

    // Initial load
    loadOperations();

    // Auto refresh
    const interval = autoRefresh ? setInterval(loadOperations, 5000) : null;

    return () => {
      eventTypes.forEach(eventType => {
        auditService.removeEventListener(eventType, handleAuditEvent);
      });
      if (interval) clearInterval(interval);
    };
  }, [loadOperations, autoRefresh]);

  // Manual refresh
  const handleRefresh = async () => {
    setRefreshing(true);
    await new Promise(resolve => setTimeout(resolve, 500)); // Small delay for UX
    loadOperations();
    setRefreshing(false);
  };

  // Toggle item details
  const handleToggleDetails = (fileId: string) => {
    setExpandedItems(prev => {
      const next = new Set(prev);
      if (next.has(fileId)) {
        next.delete(fileId);
      } else {
        next.add(fileId);
      }
      return next;
    });
  };

  // Retry operation
  const handleRetry = async (fileId: string) => {
    const operation = operations.find(op => op.fileId === fileId);
    if (operation) {
      // Log retry attempt
      await auditService.logEvent(
        AuditEventType.ERROR_RECOVERY_ATTEMPT,
        AuditSeverity.INFO,
        `Retrying operation for ${operation.fileName}`,
        {
          clientId: operation.clientId,
          workflowId: operation.workflowId,
          fileId: operation.fileId,
          fileName: operation.fileName,
          retryCount: operation.retryCount + 1
        }
      );
      
      // Trigger refresh
      loadOperations();
    }
  };

  if (operations.length === 0) {
    return (
      <Card>
        <CardContent>
          <Box sx={{ textAlign: 'center', py: 3 }}>
            <Timeline sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" color="text.secondary">
              No active file operations
            </Typography>
            <Typography variant="body2" color="text.secondary">
              File uploads and processing will appear here
            </Typography>
          </Box>
        </CardContent>
      </Card>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h6">
          File Operations {!compact && `(${operations.length})`}
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title={autoRefresh ? 'Auto-refresh enabled' : 'Auto-refresh disabled'}>
            <Button
              size="small"
              variant={autoRefresh ? 'contained' : 'outlined'}
              onClick={() => setAutoRefresh(!autoRefresh)}
              sx={{ minWidth: 'auto', px: 1 }}
            >
              Auto
            </Button>
          </Tooltip>
          <Tooltip title="Refresh">
            <IconButton 
              size="small" 
              onClick={handleRefresh}
              disabled={refreshing}
            >
              <Refresh sx={{ animation: refreshing ? 'spin 1s linear infinite' : 'none' }} />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Operations List */}
      <Box>
        {operations.map(operation => (
          <FileOperationItem
            key={operation.fileId}
            operation={operation}
            showDetails={expandedItems.has(operation.fileId)}
            onToggleDetails={() => handleToggleDetails(operation.fileId)}
            onRetry={handleRetry}
            compact={compact}
          />
        ))}
      </Box>

      {/* Show more indicator */}
      {operations.length >= maxItems && (
        <Box sx={{ textAlign: 'center', mt: 2 }}>
          <Typography variant="caption" color="text.secondary">
            Showing {maxItems} most recent operations
          </Typography>
        </Box>
      )}
    </Box>
  );
};

// CSS for spinning animation
const spinKeyframes = `
  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
`;

// Inject CSS
if (typeof document !== 'undefined') {
  const style = document.createElement('style');
  style.textContent = spinKeyframes;
  document.head.appendChild(style);
}

export default FileProgressTracker;
